import { all, call, delay, getContext, put, select } from 'redux-saga/effects';
import { IHttpApi } from '@cian/http-api/shared';
import { ILogger } from '@cian/logger';
import { getCheckServiceParams } from './utils';
import { trackBetChange, trackBetTyping } from './tracking';
import {
  CHECK_SERVICE_AFTER_CHANGE_BET,
  checkServiceFail,
  checkServiceStart,
  checkServiceSuccess,
} from '../../../actions/checkoutInfo';
import { SELECT_PUBLISH_TERMS_SERVICE, setPromotionTypeChangeReason } from '../../../actions/publishTerms';
import { changeAuctionBetSteps } from '../../../actions/auction';
import { changeCurrentBet } from '../../../thunks/auction';
import {
  selectAnalyticsOfferStatus,
  selectAnalyticsProduct,
  selectAnalyticsExtraOfferIds,
} from '../../../selectors/analytics';
import { selectBetPosition } from '../../../selectors/betPosition';
import { selectLocationIds, selectOfferCoordinatesWithFallback, selectPhotos } from '../../../selectors/offer';
import { selectPublicationTokens } from '../../../selectors/publishTerms';
import { IAnalyticsProduct, TAnalyticsOfferStatus, IAnalyticsExtraOfferIds } from '../../../types/analytics';
import { IAppEvent, IAppState, TActions } from '../../../../AppModulesWrapper';
import { IBetPosition } from '../../../types/reducers/betPosition';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import { checkService as checkServiceRequest, TCheckServiceResult } from '../../../services/checkService';
import { selectDynamicPricingData } from '../../../selectors/dynamicPricing';
import { ECheckServiceErrorCode, ECheckServiceStatus } from '../../../types/reducers/checkoutInfo';
import { changeSelectedServiceAfterCheckService } from '../../../thunks/checkout/changeSelectedServiceAfterCheckService';
import { updateBalance, updateRecommendedDepositAmount } from '../../../actions/balance';
import { selectPlacingMoneySource } from '../../../reducers/v2/moneySources/selectors';
import { logPublicationTokens } from '../../../utils/logPublicationTokens';
import { loadPublishTerms } from '../../../thunks/publishTerms/loadPublishTerms';
import { sendCheckServiceEvent } from './utils/sendCheckServiceEvent';
import { prepareParams } from '../../../thunks/checkout/checkService/utils';

export function* checkService(checkServiceAction: TActions) {
  const httpApi: IHttpApi = yield getContext('httpApi');
  const logger: ILogger = yield getContext('logger');
  const telemetry: ITelemetryLogger = yield getContext('telemetry');
  const appEvent: IAppEvent = yield getContext('appEvent');
  let state: IAppState = yield select();

  const locationPath = selectLocationIds(state);
  const photos = selectPhotos(state);
  const coordinates = selectOfferCoordinatesWithFallback(state);
  const moneySource = selectPlacingMoneySource(state);
  const extraData = selectDynamicPricingData(state);
  const publicationTokens = selectPublicationTokens(state);

  logPublicationTokens(logger, state, publicationTokens, 'checkService saga');

  const { offer, checkoutInfo } = state.servicePickerWidget;

  const isServiceChanged = checkServiceAction.type === SELECT_PUBLISH_TERMS_SERVICE;

  yield put(checkServiceStart());
  appEvent.onCheckServiceStatusChange(ECheckServiceStatus.Pending);

  const parameters = getCheckServiceParams({
    offer,
    locationPath,
    checkoutInfo,
    terms: [],
    isServiceChanged,
    photosCount: photos.length,
    coordinates,
    moneySource,
    extraData,
    publicationTokens,
  });

  const [response]: [TCheckServiceResult] = yield all([
    call(checkServiceRequest, {
      httpApi,
      logger,
      telemetry,
      parameters,
    }),
    // При быстром ответе сервера лоадер появляется на долю секунд в инпуте ставки, из-за чего происходит мерцание.
    // Для хорошего UX ограничиваем минимально возможное выполнение запроса
    delay(1000),
  ]);

  if (response.success) {
    const { selectedPromotionInfo, auction, balance, recommendedDepositAmount } = response;

    appEvent.onCheckServiceStatusChange(ECheckServiceStatus.Success);

    yield put(updateRecommendedDepositAmount(recommendedDepositAmount));

    yield put(
      changeSelectedServiceAfterCheckService({
        publicationType: selectedPromotionInfo.publicationType,
        promotionType: selectedPromotionInfo.promotionType,
        serviceId: selectedPromotionInfo.serviceId,
      }),
    );

    if (auction) {
      yield put(changeAuctionBetSteps({ previousBets: auction.previousBets, nextBets: auction.nextBets }));
      yield put(changeCurrentBet({ afterCheckService: true, bet: auction.currentBet }));
    }

    yield put(setPromotionTypeChangeReason(selectedPromotionInfo.promotionTypeChangeReason));

    yield put(checkServiceSuccess(response.payload));

    yield put(updateBalance(balance));

    if (checkServiceAction.type === CHECK_SERVICE_AFTER_CHANGE_BET) {
      const analyticsOfferStatus: TAnalyticsOfferStatus = yield select(selectAnalyticsOfferStatus);
      const analyticsProduct: IAnalyticsProduct = yield select(selectAnalyticsProduct);
      const analyticsExtraOfferIds: IAnalyticsExtraOfferIds = yield select(selectAnalyticsExtraOfferIds);
      const betPosition: IBetPosition | null = yield select(selectBetPosition);

      const { currentBet } = auction;
      const { offerPosition = null, page = null } = betPosition || {};

      if (checkServiceAction.payload === 'input') {
        trackBetTyping({
          analyticsExtraOfferIds,
          analyticsOfferStatus,
          analyticsProduct,
          selectedServiceId: selectedPromotionInfo.serviceId,
          currentBet,
          offerPosition,
          page,
        });
      } else {
        // Трекаем событие клика по кнопкам поля ввода ставки
        trackBetChange({
          analyticsExtraOfferIds,
          analyticsOfferStatus,
          analyticsProduct,
          sign: checkServiceAction.payload,
          selectedServiceId: selectedPromotionInfo.serviceId,
          currentBet,
          offerPosition,
          page,
        });
      }
    }
  } else {
    if (response.payload === ECheckServiceErrorCode.tokenExpired) {
      yield put(
        loadPublishTerms({
          isServicesReload: true,
        }),
      );

      return;
    }
    yield put(checkServiceFail(response.payload));
    appEvent.onCheckServiceStatusChange(ECheckServiceStatus.Fail);
  }

  state = yield select();
  sendCheckServiceEvent(state, response, logger);

  const newState: IAppState = yield select();
  const params = prepareParams(newState);

  appEvent.onCheckServiceChange(params);
}
